import { GroupType } from '@bp/bp-graphql-types';
import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  type DropdownMenuItem,
  MeetingDefaultIcon,
  type Row,
  Table,
  type TableColumns,
} from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useConfirm } from 'hooks/useConfirm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copyEventLinkToClipboard, endEvent, startAndJoinEvent, startEvent } from 'utils/eventHelper';
import styles from './RunningMeetingsTable.module.scss';

const RunningMeetingsTableFieldsDocument = graphql(`
  fragment RunningMeetingsTableFields on Query {
    calendarEvents(where: $calendarEventWhere) {
      __typename
      uuid
      start
      title
      maxAttendees
      holderConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            ... on Group {
              uuid
              displayName
              type
            }
            ... on Organization {
              uuid
              displayName
            }
            ... on Profile {
              uuid
              displayName
            }
          }
        }
      }
      virtualLocations {
        uuid
        __typename
        running
        token
        attendeesConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              withVideo
            }
            node {
              uuid
              __typename
            }
          }
        }
      }
    }
  }
`);

type RunningMeetingType = {
  eventUuid: string;
  originUuid: string;
  locationUuid: string;
  running: boolean;
  origin: 'Profile' | 'Organization' | GroupType | undefined;
  originName: string;
  eventName: string;
  start: string;
  attendees: number;
  withVideo: number;
  maxAttendees: number;
  uri: string;
  token: string;
};

type RunningMeetingsTableProps = {
  type: 'bbb' | 'zoom';
  onRefetch: () => void;
  query: FragmentType<typeof RunningMeetingsTableFieldsDocument> | undefined;
};

export const RunningMeetingsTable = ({ query, type, onRefetch }: RunningMeetingsTableProps) => {
  const { t } = useTranslation();
  const profile = useAuthClaims().pimAuthClaims.getProfile();

  const [loading, setLoading] = useState<boolean>(false);

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('meetings.titleShort'),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const calendarEvents = getFragmentData(RunningMeetingsTableFieldsDocument, query)?.calendarEvents ?? [];

  const meetings = calendarEvents
    .filter((event) => {
      return dayjs(event.start).isSameOrAfter(dayjs(), 'days');
    })
    .sort((a, b) => dayjs(a.start).diff(b.start))
    .flatMap((event) => {
      const holderNode = event.holderConnection.edges.find((h) => (h.node.uuid ?? '').length > 0)?.node;
      return event.virtualLocations.flatMap((location) => {
        return {
          eventUuid: event.uuid,
          originUuid: holderNode?.uuid ?? '',
          locationUuid: location.uuid,
          running: location.running,
          origin: holderNode?.__typename === 'Group' ? holderNode.type : holderNode?.__typename,
          originName: holderNode?.displayName ?? '',
          eventName: event.title ?? '',
          start: dayjs(event.start).format('DD.MM.YY, HH:mm'),
          attendees: location.attendeesConnection.edges.length,
          withVideo: location.attendeesConnection.edges.filter((e) => e.properties.withVideo).length,
          maxAttendees: event.maxAttendees ?? 0,
          uri: import.meta.env.VITE_APP_REDIRECT_URI + '/join/' + location.token,
          token: location.token,
        } satisfies RunningMeetingType;
      });
    });

  const bbbColumns: TableColumns<RunningMeetingType>[] = [
    {
      header: t('meetings.origin'),
      id: 'origin',
      accessorKey: 'origin',
      cell: ({ row }) => {
        switch (row.original.origin) {
          case 'Profile':
            return t('persons.title', { count: 1 });
          case GroupType.Course:
            return t('courses.titleSingular');
          case GroupType.Group:
            return t('groups.titleSingular');
          default:
            break;
        }
      },
      size: 75,
    },
    {
      header: t('meetings.originName'),
      id: 'originName',
      accessorKey: 'originName',
      size: 250,
    },
    {
      header: t('meetings.eventName'),
      id: 'eventName',
      accessorKey: 'eventName',
      size: 350,
    },
    {
      header: t('meetings.table.plannedBegin', { context: 'short' }),
      id: 'start',
      accessorKey: 'start',
      size: 120,
      meta: {
        tooltip: t('meetings.table.plannedBegin', { context: 'long' }),
      },
    },
    {
      header: t('meetings.table.attendees', { context: 'short' }),
      id: 'attendees',
      accessorKey: 'attendees',
      size: 50,
      meta: {
        tooltip: t('meetings.table.attendees', { context: 'long' }),
      },
    },
    {
      header: t('meetings.table.withVideo'),
      id: 'withVideo',
      accessorKey: 'withVideo',
      size: 50,
    },
    {
      header: t('meetings.table.maxAttendees', { context: 'short' }),
      id: 'maxAttendees',
      accessorKey: 'maxAttendees',
      size: 50,
      meta: {
        tooltip: t('meetings.table.maxAttendees', { context: 'long' }),
      },
    },
    {
      header: t('meetings.table.running'),
      id: 'running',
      accessorKey: 'running',
      type: 'active',
    },
  ];

  // TODO: adjust when zoom-meetings are more defined
  const zoomColumns: TableColumns<RunningMeetingType>[] = [
    {
      header: t('meetings.table.licenseFrom'),
      id: 'licenseFrom',
      accessorKey: 'licenseFrom',
    },
    {
      header: t('meetings.moderator'),
      id: 'moderator',
      accessorKey: 'moderator',
    },
    {
      header: t('common.started'),
      id: 'start',
      accessorKey: 'start',
    },
    {
      header: t('common.status'),
      id: 'status',
      accessorKey: 'status',
    },
  ];

  const menu = ({ uri, token, running }: RunningMeetingType): DropdownMenuItem[] => {
    return [
      {
        label: t('meetings.start'),
        onClick: async () => {
          await startEvent(token);
          onRefetch();
        },
        disabled: running,
      },
      {
        label: t('meetings.copyLink'),
        onClick: () => copyEventLinkToClipboard(uri),
      },
      {
        label: t('meetings.joinAsModerator'),
        onClick: async () => {
          await onJoin(token, true);
          onRefetch();
        },
        disabled: !running,
      },
      {
        label: t('meetings.joinAsMember'),
        onClick: async () => {
          await onJoin(token, false);
          onRefetch();
        },
        disabled: !running,
      },
      { type: 'ruler' },
      {
        label: t('meetings.endImmediately'),
        color: 'error',
        onClick: async () => {
          const res = await confirmDelete();
          if (res) {
            await onEnd(token);
            onRefetch();
          }
        },
        disabled: !running,
      },
    ];
  };

  async function onJoin(token: string, asModerator: boolean) {
    setLoading(true);
    await startAndJoinEvent(token, profile, asModerator);
    setLoading(false);
  }

  async function onEnd(token: string) {
    setLoading(true);
    await endEvent(token);
    setLoading(false);
  }

  return (
    <>
      <Table<RunningMeetingType>
        hideHeader={meetings.length === 0}
        columns={type === 'bbb' ? bbbColumns : zoomColumns}
        data={meetings}
        emptyStateSettings={{
          title: t('meetings.noRunningMeetings'),
          icon: <MeetingDefaultIcon />,
          subtitle: '',
        }}
        showActionBar
        canScroll={meetings.length !== 0}
        customPadding='var(--spacing-6)'
        lastCol={(row: Row<RunningMeetingType>) => {
          return (
            <Dropdown
              className={styles['drop-down']}
              trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon />} isLoading={loading} />}
            >
              <DropdownMenu data={menu(row.original)} />
            </Dropdown>
          );
        }}
      />

      <ConfirmationDialog />
    </>
  );
};
